if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
        this.onGalleryClick = this.onGalleryClick.bind(this);
        this.onGalleryKeydown = this.onGalleryKeydown.bind(this);
        this.stopGalleryPointerEvent = this.stopGalleryPointerEvent.bind(this);
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        this.gallery = this.querySelector('[data-modal-gallery]');
        this.gallery?.addEventListener('click', this.onGalleryClick);
        this.gallery?.addEventListener('pointerup', this.stopGalleryPointerEvent);
        this.addEventListener('keydown', this.onGalleryKeydown);
      }

      disconnectedCallback() {
        this.gallery?.removeEventListener('click', this.onGalleryClick);
        this.gallery?.removeEventListener('pointerup', this.stopGalleryPointerEvent);
        this.removeEventListener('keydown', this.onGalleryKeydown);
        if (super.disconnectedCallback) super.disconnectedCallback();
      }

      stopGalleryPointerEvent(event) {
        // Dawn's ModalDialog closes media modals on any mouse pointerup
        // outside deferred-media/product-model. Keep gallery controls interactive.
        event.stopPropagation();
      }

      get slides() {
        return Array.from(this.querySelectorAll('[data-modal-slide]'));
      }

      get thumbnails() {
        return Array.from(this.querySelectorAll('[data-modal-thumbnail]'));
      }

      show(opener) {
        super.show(opener);
        const mediaId = opener?.getAttribute('data-media-id');
        const matchingIndex = this.slides.findIndex(
          (slide) => slide.dataset.galleryMediaId === mediaId
        );
        this.activateSlide(matchingIndex >= 0 ? matchingIndex : 0, false);
      }

      hide() {
        this.pauseAllMedia();
        super.hide();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const matchingIndex = this.slides.findIndex(
          (slide) => slide.dataset.galleryMediaId === mediaId
        );
        this.activateSlide(matchingIndex >= 0 ? matchingIndex : 0, false);
      }

      onGalleryClick(event) {
        event.stopPropagation();

        const previous = event.target.closest('[data-modal-previous]');
        const next = event.target.closest('[data-modal-next]');
        const thumbnail = event.target.closest('[data-modal-thumbnail]');

        if (previous) {
          event.preventDefault();
          this.activateSlide(this.currentIndex - 1);
          return;
        }

        if (next) {
          event.preventDefault();
          this.activateSlide(this.currentIndex + 1);
          return;
        }

        if (thumbnail) {
          event.preventDefault();
          const targetId = thumbnail.dataset.targetMediaId;
          const targetIndex = this.slides.findIndex(
            (slide) => slide.dataset.galleryMediaId === targetId
          );
          if (targetIndex >= 0) this.activateSlide(targetIndex);
        }
      }

      onGalleryKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.activateSlide(this.currentIndex - 1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.activateSlide(this.currentIndex + 1);
        }
      }

      activateSlide(requestedIndex, focusThumbnail = true) {
        const slides = this.slides;
        if (!slides.length) return;

        const index = (requestedIndex + slides.length) % slides.length;
        this.pauseAllMedia();
        this.currentIndex = index;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;
          slide.classList.toggle('active', isActive);
          slide.hidden = !isActive;
          slide.setAttribute('aria-hidden', String(!isActive));
        });

        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === index;
          thumbnail.classList.toggle('active', isActive);
          if (isActive) {
            thumbnail.setAttribute('aria-current', 'true');
            thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            if (focusThumbnail) thumbnail.focus({ preventScroll: true });
          } else {
            thumbnail.removeAttribute('aria-current');
          }
        });

        const counter = this.querySelector('[data-modal-current]');
        if (counter) counter.textContent = String(index + 1);

        this.playMedia(slides[index]);
      }

      playMedia(slide) {
        if (!slide) return;
        const deferredMedia = slide.querySelector('deferred-media');

        if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
          deferredMedia.loadContent(false);
        }

        window.requestAnimationFrame(() => {
          const video = slide.querySelector('video');
          if (video) {
            video.muted = true;
            video.controls = true;
            const playPromise = video.play();
            if (playPromise?.catch) playPromise.catch(() => {});
          }

          const iframe = slide.querySelector('iframe');
          if (iframe) {
            iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
            const startExternalVideo = () => {
              iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'mute', args: [] }),
                '*'
              );
              iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
                '*'
              );
              iframe.contentWindow?.postMessage({ method: 'setVolume', value: 0 }, '*');
              iframe.contentWindow?.postMessage({ method: 'play' }, '*');
            };
            iframe.addEventListener('load', startExternalVideo, { once: true });
            startExternalVideo();
          }
        });
      }

      pauseAllMedia() {
        this.querySelectorAll('video').forEach((video) => video.pause());
        this.querySelectorAll('iframe').forEach((iframe) => {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
          iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
        });
      }
    }
  );
}
